import { Briefcase, CalendarDays, ClipboardList, Coffee, Package, User, Users, Wrench } from "lucide-react";
import type { ComponentType } from "react";
import type { ReportType } from "@normax-performance/shared-types";
import type { ResolvableReportPeriodPresetKey } from "./period";

export interface ReportTypeMeta {
  reportType: ReportType;
  titleKey: string;
  requiresEntity: "worker" | "product" | "operation" | null;
  /** DAILY_PRODUCTION defaults to `lastRecordedDay` (not `yesterday`) so the
   * report opens honest about weekend/late-entry lag from the first render —
   * the exact same reasoning already applied to Dashboard's own default
   * (docs/NORMAX_PERFORMANCE_REPORTING_DAY_DASHBOARD_IMPLEMENTATION_REPORT.md).
   * MONTHLY_MANAGEMENT defaults to `previousMonth`, matching the brief's
   * stated business default (§12: "PREVIOUS_MONTH"). */
  defaultPeriodPreset: ResolvableReportPeriodPresetKey | "lastRecordedDay";
  /** i18n key under `reports:landing.cards.*` — a one-line, non-marketing
   * descriptor shown on the landing page's quick-report card (presentation
   * polish pass, premium-reports-presentation-polish). */
  descriptorKey: string;
  /** Quiet, single-color glyph for the landing card — reuses the same
   * `lucide-react` icon set already established in `AppShell.tsx`'s nav,
   * never a colored/illustrated icon. */
  icon: ComponentType<{ size?: number; "aria-hidden"?: boolean | "true" | "false" }>;
}

export const REPORT_TYPE_REGISTRY: ReportTypeMeta[] = [
  { reportType: "WORKER", titleKey: "reportTypes.worker", requiresEntity: "worker", defaultPeriodPreset: "thisMonth", descriptorKey: "landing.cards.worker", icon: User },
  { reportType: "ALL_WORKERS", titleKey: "reportTypes.allWorkers", requiresEntity: null, defaultPeriodPreset: "thisMonth", descriptorKey: "landing.cards.allWorkers", icon: Users },
  { reportType: "PRODUCT", titleKey: "reportTypes.product", requiresEntity: "product", defaultPeriodPreset: "thisMonth", descriptorKey: "landing.cards.product", icon: Package },
  { reportType: "OPERATION", titleKey: "reportTypes.operation", requiresEntity: "operation", defaultPeriodPreset: "thisMonth", descriptorKey: "landing.cards.operation", icon: Wrench },
  { reportType: "DAILY_PRODUCTION", titleKey: "reportTypes.dailyProduction", requiresEntity: null, defaultPeriodPreset: "lastRecordedDay", descriptorKey: "landing.cards.dailyProduction", icon: CalendarDays },
  { reportType: "MONTHLY_MANAGEMENT", titleKey: "reportTypes.monthlyManagement", requiresEntity: null, defaultPeriodPreset: "previousMonth", descriptorKey: "landing.cards.monthlyManagement", icon: Briefcase },
  { reportType: "NEUTRAL_ACTIVITIES", titleKey: "reportTypes.neutralActivities", requiresEntity: null, defaultPeriodPreset: "thisMonth", descriptorKey: "landing.cards.neutralActivities", icon: Coffee },
  { reportType: "UNNORMED_WORK", titleKey: "reportTypes.unnormedWork", requiresEntity: null, defaultPeriodPreset: "thisMonth", descriptorKey: "landing.cards.unnormedWork", icon: ClipboardList },
];

export function getReportTypeMeta(reportType: ReportType): ReportTypeMeta {
  const meta = REPORT_TYPE_REGISTRY.find((r) => r.reportType === reportType);
  if (!meta) throw new Error(`Unknown report type: ${reportType}`);
  return meta;
}
